import { z } from 'zod';

import { scraperConfigSchema } from './scraper-config.schema';

// A `scrapers` table row: a named, reusable scraper config that one or more
// chatbot tools (tool_type='scraper') can be assigned to via `scraper_id`.

const nameSchema = z
  .string()
  .min(2, 'Scraper name must be at least 2 characters')
  .max(100, 'Scraper name must be at most 100 characters')
  .regex(
    /^[a-z_][a-z0-9_]*$/,
    'Scraper name must be snake_case (lowercase letters, numbers, and underscores only)',
  );

export const scraperSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  config: scraperConfigSchema,
  is_active: z.boolean(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});
export type Scraper = z.infer<typeof scraperSchema>;

export const createScraperSchema = z.object({
  name: nameSchema,
  description: z.string().min(10, 'Description must be at least 10 characters'),
  config: scraperConfigSchema,
  is_active: z.boolean().optional().default(true),
});
export type CreateScraperDto = z.infer<typeof createScraperSchema>;

export const updateScraperSchema = z
  .object({
    name: nameSchema,
    description: z.string().min(10),
    config: scraperConfigSchema,
    is_active: z.boolean(),
  })
  .partial();
export type UpdateScraperDto = z.infer<typeof updateScraperSchema>;

export const scraperQuerySchema = z.object({
  search: z.string().optional(),
  is_active: z.coerce.boolean().optional(),
});
export type ScraperQuery = z.infer<typeof scraperQuerySchema>;

// Ad-hoc test: run a config with sample args without persisting it.
export const testScraperSchema = z.object({
  config: scraperConfigSchema,
  args: z.record(z.string(), z.unknown()).optional().default({}),
});
export type TestScraperDto = z.infer<typeof testScraperSchema>;

export interface ScraperRunResult {
  success: boolean;
  records?: Record<string, unknown>[];
  record_count?: number;
  pages_fetched?: number;
  truncated?: boolean;
  error?: string;
}

// Export/import (transfer between environments) — no ids/timestamps.
export const scraperExportItemSchema = z.object({
  name: nameSchema,
  description: z.string(),
  config: scraperConfigSchema,
  is_active: z.boolean().optional().default(true),
});
export const scraperImportSchema = z.object({
  scrapers: z.array(scraperExportItemSchema).min(1, 'At least one scraper is required'),
});
export type ScraperImportDto = z.infer<typeof scraperImportSchema>;
